import React, { useState } from 'react';
import axios from 'axios';
import Footer from './Footer';

const Mentorship = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    expertise: '',
    experience: '',
    motivation: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [activeTab, setActiveTab] = useState(null);

  const expertiseOptions = [
    'Sound Engineering & Mixing',
    'Videography & Filmmaking for Worship',
    'Music Production & Arrangement',
    'Stage Lighting & Visuals',
    'Music Theory & Worship Leadership',
    'Other'
  ];

  const toggleTab = (tab) => {
    setActiveTab(activeTab === tab ? null : tab);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.expertise) newErrors.expertise = 'Please select an area of expertise';
    if (!formData.experience.trim()) newErrors.experience = 'Please share your experience';
    if (!formData.motivation.trim()) newErrors.motivation = 'Please tell us why you want to train our band';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/mentorship/register`, 
        formData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        setSuccessMessage('Registration successful! We will contact you soon.');
        setProgress(100);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          expertise: '',
          experience: '',
          motivation: ''
        });
        
        setTimeout(() => {
          setProgress(0);
          setSuccessMessage('');
        }, 10000);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to submit registration!');
      setProgress(100);
      setTimeout(() => {
        setProgress(0);
        setErrorMessage('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
    <div className="flex items-center justify-center pt-[120px] min-h-screen bg-black">
      <div className="bg-black border border-gray-700 bg-opacity-80 text-white p-8 rounded-lg max-w-2xl w-full mx-4">
        <h2 className="text-3xl text-center mb-6 font-greatvibes">Mentorship & Training</h2>
        
        {/* Collapsible Sections */}
        <div className="mb-6 space-y-2">
          {/* Description Section */}
          <div className="border border-gray-700 rounded">
            <button
              onClick={() => toggleTab('description')}
              className="w-full text-left p-3 flex justify-between items-center hover:bg-gray-800"
            >
              <span className="font-medium">Description</span>
              <span>{activeTab === 'description' ? '−' : '+'}</span>
            </button>
            {activeTab === 'description' && (
              <div className="p-4 border-t border-gray-700 text-gray-300">
                <p>
                  We believe that growth is a journey, and we are constantly looking to improve our skills and enrich our worship experience. If you are a professional in sound engineering, videography, music production, or any other field that can contribute to the success of our ministry, we'd love to connect with you! Join us as a mentor or trainer and help us grow and serve the Lord in excellency.
                </p>
              </div>
            )}
          </div>
          
          {/* Why Join Us Section */}
          <div className="border border-gray-700 rounded">
            <button
              onClick={() => toggleTab('whyJoin')}
              className="w-full text-left p-3 flex justify-between items-center hover:bg-gray-800"
            >
              <span className="font-medium">Why Join Us</span>
              <span>{activeTab === 'whyJoin' ? '−' : '+'}</span>
            </button>
            {activeTab === 'whyJoin' && (
              <div className="p-4 border-t border-gray-700 text-gray-300">
                <p>
                  By joining our team of mentors and trainers, you'll have the opportunity to impact a dynamic and passionate group of individuals. Whether you're teaching sound engineering techniques, helping with music production, or sharing videography skills, your experience will help us create better, more impactful worship experiences for the body of Christ.
                </p>
              </div>
            )}
          </div>
          
          {/* Fields of Expertise Section */}
          <div className="border border-gray-700 rounded">
            <button
              onClick={() => toggleTab('expertise')}
              className="w-full text-left p-3 flex justify-between items-center hover:bg-gray-800"
            >
              <span className="font-medium">Fields of Expertise We're Seeking</span>
              <span>{activeTab === 'expertise' ? '−' : '+'}</span>
            </button>
            {activeTab === 'expertise' && (
              <div className="p-4 border-t border-gray-700 text-gray-300">
                <p>We are looking for professionals who can guide us in the following areas:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  {expertiseOptions.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {/* How to Get Involved Section */}
          <div className="border border-gray-700 rounded">
            <button
              onClick={() => toggleTab('getInvolved')}
              className="w-full text-left p-3 flex justify-between items-center hover:bg-gray-800"
            >
              <span className="font-medium">How to Get Involved</span>
              <span>{activeTab === 'getInvolved' ? '−' : '+'}</span>
            </button>
            {activeTab === 'getInvolved' && (
              <div className="p-4 border-t border-gray-700 text-gray-300">
                <p>
                  If you have experience in any of these areas and passionate about helping others grow, please get in touch with us! Fill out the registration form below or reach out via email at <span className="text-yellow-400">info@elayonemusic.org</span>. We would love to hear from you and explore how we can collaborate to make our worship experiences even better!
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Registration Form */}
        <h3 className="text-xl mb-4 text-center font-medium">Registration Form</h3>
        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          <div>
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full border ${errors.name ? 'border-red-500' : 'border-gray-700'} p-2 rounded bg-transparent text-white placeholder-gray-400 focus:outline-none focus:border-gray-500`}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>
          
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              className={`w-full border ${errors.email ? 'border-red-500' : 'border-gray-700'} p-2 rounded bg-transparent text-white placeholder-gray-400 focus:outline-none focus:border-gray-500`}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>
          
          <div>
            <select
              name="expertise"
              value={formData.expertise}
              onChange={handleChange}
              className={`w-full border ${errors.expertise ? 'border-red-500' : 'border-gray-700'} p-2 rounded bg-transparent text-white focus:outline-none focus:border-gray-500`}
            >
              <option value="">Select Area of Expertise</option>
              {expertiseOptions.map((option, index) => (
                <option key={index} value={option} className="bg-black">{option}</option>
              ))}
            </select>
            {errors.expertise && <p className="text-red-500 text-sm mt-1">{errors.expertise}</p>}
          </div>
          
          <div>
            <textarea
              name="experience"
              placeholder="Experience"
              value={formData.experience}
              onChange={handleChange}
              rows="3"
              className={`w-full border ${errors.experience ? 'border-red-500' : 'border-gray-700'} p-2 rounded bg-transparent text-white placeholder-gray-400 focus:outline-none focus:border-gray-500`}
            />
            {errors.experience && <p className="text-red-500 text-sm mt-1">{errors.experience}</p>}
          </div>
          
          <div>
            <textarea
              name="motivation"
              placeholder="Why you want to train our band"
              value={formData.motivation}
              onChange={handleChange}
              rows="3"
              className={`w-full border ${errors.motivation ? 'border-red-500' : 'border-gray-700'} p-2 rounded bg-transparent text-white placeholder-gray-400 focus:outline-none focus:border-gray-500`}
            />
            {errors.motivation && <p className="text-red-500 text-sm mt-1">{errors.motivation}</p>}
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-gray-700 text-gray-300 text-sm py-2 px-6 rounded hover:bg-gray-600 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
        
        {/* Success Message Popup */}
        {successMessage && (
          <div className="fixed top-6 left-0 right-0 mx-auto w-64 bg-green-100 text-green-900 border-green-500 pt-2 rounded-t-md shadow-md z-50">
            <div className="flex justify-center items-center">
              <span className="text-center p-1">{successMessage}</span>
            </div>
            <div className="h-1 mt-1 bg-green-300">
              <div style={{ width: `${progress}%` }} className="h-full bg-green-700 transition-all duration-500" />
            </div>
          </div>
        )}

        {/* Error Message Popup */}
        {errorMessage && (
          <div className="fixed top-6 left-0 right-0 mx-auto w-64 bg-red-100 text-red-900 border-red-500 pt-2 rounded-t-md shadow-md z-50">
            <div className="flex justify-center items-center">
              <span className="text-center p-1">{errorMessage}</span>
            </div>
            <div className="h-1 mt-1 bg-red-300">
              <div style={{ width: `${progress}%` }} className="h-full bg-red-700 transition-all duration-500" />
            </div>
          </div>
        )}
      </div>
     
    </div>
     <Footer />
     </div>
  );
};

export default Mentorship;